/* eslint-disable react/no-array-index-key */
'use client';

import c from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GalleryProps } from './Gallery.types';
import { GalleryControls } from './GalleryControls';
import GallerySlide from './GallerySlide';
import { GalleryThumbnailButton } from './GalleryThumbnailButton';

export const Gallery = ({
  position = 'details',
  slides,
  microcopy,
  className,
  hasCookieConsent,
  isVerifiedSolution = false,
  aspectRatio = 'aspect-video',
  onOpenCookieSettings,
}: GalleryProps) => {
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideAnnouncement, setSlideAnnouncement] = useState('');

  useEffect(() => {
    const scrollThumbnailButtonsListener = (event: WheelEvent) => {
      const TABLET_SCREEN_BOUNDARY = 1024;
      if (
        thumbnailContainerRef?.current &&
        window.innerWidth < TABLET_SCREEN_BOUNDARY
      ) {
        event.preventDefault();
        thumbnailContainerRef.current.scrollLeft += event.deltaY;
      }
    };

    const thumbnailContainer = thumbnailContainerRef.current;
    thumbnailContainer?.addEventListener(
      'wheel',
      scrollThumbnailButtonsListener
    );

    return () => {
      thumbnailContainer?.removeEventListener(
        'wheel',
        scrollThumbnailButtonsListener
      );
    };
  }, []);

  const scrollToThumbnail = useCallback((index: number) => {
    const thumbnailButton = document.getElementById(
      `gallery-thumbnail-${index}`
    );

    if (thumbnailButton && thumbnailContainerRef?.current) {
      const container = thumbnailContainerRef.current;
      const isDesktop = window.innerWidth >= 1024;

      if (isDesktop) {
        // Desktop: scroll vertically to center the thumbnail
        const containerRect = container.getBoundingClientRect();
        const buttonRect = thumbnailButton.getBoundingClientRect();
        const scrollTop =
          container.scrollTop +
          (buttonRect.top - containerRect.top) -
          containerRect.height / 2 +
          buttonRect.height / 2;

        container.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth',
        });
      } else {
        // Mobile: scroll horizontally to center the thumbnail
        const containerRect = container.getBoundingClientRect();
        const buttonRect = thumbnailButton.getBoundingClientRect();
        const scrollLeft =
          container.scrollLeft +
          (buttonRect.left - containerRect.left) -
          containerRect.width / 2 +
          buttonRect.width / 2;

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        });
      }
    }
  }, []);

  const handleSetSlideIndex = useCallback(
    (index: number) => {
      setSlideIndex(index);
      setSlideAnnouncement(
        microcopy.slideAnnouncement
          .replace('{{current}}', String(index + 1))
          .replace('{{total}}', String(slides.length))
      );

      // Use setTimeout to ensure DOM is updated before scrolling
      setTimeout(() => {
        scrollToThumbnail(index);
      }, 0);
    },
    [scrollToThumbnail, microcopy.slideAnnouncement, slides.length]
  );

  const focusSlideContent = useCallback((index: number) => {
    const slide = document.getElementById(`gallery-slide-${index}`);
    if (slide) {
      slide.tabIndex = -1;
      setTimeout(() => {
        slide.focus();
      }, 0);
    }
  }, []);

  const nextSlide = useCallback(() => {
    handleSetSlideIndex(slideIndex === slides.length - 1 ? 0 : slideIndex + 1);
  }, [handleSetSlideIndex, slideIndex, slides.length]);

  const previousSlide = useCallback(() => {
    handleSetSlideIndex(slideIndex === 0 ? slides.length - 1 : slideIndex - 1);
  }, [handleSetSlideIndex, slideIndex, slides.length]);

  const aspectRatioThumbsMargins = () => {
    switch (aspectRatio) {
      case 'aspect-video':
      case 'aspect-[4/3]':
      case 'aspect-[3/2]':
      default:
        return 'mb-0';
    }
  };

  if (slides.length === 0) {
    return null;
  }

  return (
    <section
      role="group"
      aria-label={microcopy.galleryAriaLabel}
      className={c(
        'relative grid w-full grid-cols-8 lg:gap-x-3 lg:p-0',
        className
      )}
    >
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {slideAnnouncement}
      </div>
      {slides.length > 1 && (
        <div
          ref={thumbnailContainerRef}
          className={c(
            `${slides.length > 1 ? `light-custom-scrollbar hide-scrollbar relative order-3 col-span-8 ${aspectRatioThumbsMargins()} lg-h-auto min-h-0 overflow-auto lg:order-1 lg:col-span-1 lg:mx-0 lg:mt-0 lg:h-full lg:min-h-14 lg:pt-0` : 'hidden'}`
          )}
        >
          <ul className="flex w-full gap-3 bg-surface-muted px-4 pb-1 pt-4 lg:absolute lg:w-full lg:flex-col lg:gap-1 lg:bg-canvas lg:px-1 lg:pl-2 lg:pt-1">
            {slides.map((slide, index) => (
              <li key={`${slide.src}-${index}`}>
                <GalleryThumbnailButton
                  id={`gallery-thumbnail-${index}`}
                  currentSlide={slideIndex}
                  setCurrentSlide={() => {
                    handleSetSlideIndex(index);
                    focusSlideContent(index);
                  }}
                  slide={slide}
                  index={index}
                  aspectRatio={aspectRatio}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
      <div
        className={c(
          `${slides.length > 1 ? 'order-3 col-span-8 flex flex-col lg:col-span-7 lg:col-start-2 lg:mx-0' : 'hidden'}`
        )}
      >
        {slides.length > 1 && (
          <GalleryControls
            currentSlide={slideIndex}
            totalSlides={slides.length}
            nextSlide={nextSlide}
            previousSlide={previousSlide}
            previousAriaLabel={microcopy.previousAriaLabel}
            nextAriaLabel={microcopy.nextAriaLabel}
          />
        )}
      </div>
      <div
        className={c(
          `${slides.length > 1 ? 'order-1 col-span-8 flex flex-col px-4 pt-4 lg:col-span-7 lg:col-start-2 lg:mx-0 lg:mt-0 lg:p-0' : 'col-span-8 col-start-1 lg:p-0'}`,
          'bg-surface-muted',
          aspectRatio
        )}
      >
        <div className="flex h-full w-full overflow-hidden">
          {slides.map((slide, index) => (
            <GallerySlide
              key={`${slide.src}-${index}`}
              id={`gallery-slide-${index}`}
              isHidden={slideIndex !== index}
              style={index === 0 ? { marginLeft: `-${slideIndex * 100}%` } : {}}
              position={position}
              slide={slide}
              showVerifiedSolutionOverlay={isVerifiedSolution && index === 0}
              hasCookieConsent={hasCookieConsent}
              videoCookieOverlayMicrocopy={microcopy.videoCookieOverlay}
              onOpenCookieSettings={onOpenCookieSettings}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
